import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import LineItemSelect from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TrinitiDatePicker, TrinitiTimePicker } from '@/components/triniti-datetime';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { Home, Users, Calendar, Clock, Plus, Eye, AlertTriangle, CheckCircle, MapPin, UserPlus, RefreshCw } from 'lucide-react';
import { ShiftPeriodBadgeForShift } from '@/components/calendar/ShiftPeriodBadge';
import { SilRatioInlineWarning, RatioRulesScopeNote } from '@/components/calendar/SilRatioSafetyPanel';

// Day 13 / client#17: active residents of a house, all pre-selected — shared by the single and recurring dialogs.
function useHouseResidents(open, houseId) {
  const [supportContext, setSupportContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [selectedResidents, setSelectedResidents] = useState([]);
  useEffect(() => {
    if (open && houseId) {
      setLoadingContext(true);
      api.get(`/accommodation/houses/${houseId}/support-context`)
        .then(res => {
          const ctx = res.data.data;
          setSupportContext(ctx);
          // Auto-select all residents by default
          setSelectedResidents((ctx?.residents || []).map(r => r.participant_id));
        })
        .catch(err => {
          console.warn('Failed to fetch support context:', err);
          setSupportContext(null);
          setSelectedResidents([]);
        })
        .finally(() => setLoadingContext(false));
    }
  }, [open, houseId]);
  return { supportContext, loadingContext, selectedResidents, setSelectedResidents };
}

function LinkedResidentsPicker({ supportContext, loadingContext, selectedResidents, setSelectedResidents, testIdPrefix = 'resident' }) {
  const toggleResident = (participantId) => {
    setSelectedResidents(prev =>
      prev.includes(participantId)
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };
  return (
    <div className="p-3 bg-green-50 border border-green-200 rounded-lg" data-testid={`${testIdPrefix === 'resident' ? 'linked-residents' : testIdPrefix}-section`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="flex items-center gap-2 text-green-700">
          <Users className="w-4 h-4" />
          Linked Residents
        </Label>
        {loadingContext && (
          <span className="text-xs text-muted-foreground animate-pulse">Loading...</span>
        )}
      </div>

      {supportContext?.residents?.length > 0 ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {supportContext.residents.map(r => (
              <label
                key={r.participant_id}
                className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                  selectedResidents.includes(r.participant_id)
                    ? 'bg-green-100 border-green-400'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
                data-testid={`${testIdPrefix}-${r.participant_id}`}
              >
                <input
                  type="checkbox"
                  checked={selectedResidents.includes(r.participant_id)}
                  onChange={() => toggleResident(r.participant_id)}
                  className="rounded text-green-600"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.participant_name || 'Unknown'}</p>
                  {r.room_name && (
                    <p className="text-xs text-muted-foreground">Room: {r.room_name}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
            <span>{selectedResidents.length} of {supportContext.residents.length} selected</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelectedResidents(supportContext.residents.map(r => r.participant_id))}
                className="text-green-600 hover:underline"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={() => setSelectedResidents([])}
                className="text-red-600 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      ) : loadingContext ? (
        <div className="text-sm text-muted-foreground py-2">Loading residents...</div>
      ) : (
        <div className="text-sm text-amber-600 py-2 flex items-center gap-2" data-testid={`${testIdPrefix}-none`}>
          <AlertTriangle className="w-4 h-4" />
          No active residents in this house — add a resident placement to the house first
        </div>
      )}
    </div>
  );
}

function CreateHouseShiftDialog({ house, open, onClose, services, onSuccess }) {
  const [form, setForm] = useState({
    service_id: '',
    service_resolution_mode: 'PER_PARTICIPANT',
    shift_date: '',
    start_time: '06:00',
    end_time: '14:00',
    required_staff: house?.recommended_staff || 1,
    support_ratio: '1:3',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  
  // Day 13: Multi-participant support state
  const residents = useHouseResidents(open, house?.id);
  const { supportContext, loadingContext, selectedResidents } = residents;

  useEffect(() => {
    if (house) {
      setForm(f => ({
        ...f,
        required_staff: house.recommended_staff || 1,
      }));
    }
  }, [house]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isPerParticipant = form.service_resolution_mode === 'PER_PARTICIPANT';
    if ((!isPerParticipant && !form.service_id) || !form.shift_date) {
      notify.warn('SIL_SHIFT_VALIDATION');
      return;
    }
    setLoading(true);
    try {
      // Build Day 13 multi-participant fields
      const linkedResidents = supportContext?.residents?.filter(r => 
        selectedResidents.includes(r.participant_id)
      ) || [];
      const primaryParticipantId = selectedResidents[0] || null;

      await api.post('/roster/sil/create-shift', {
        house_id: house.id,
        service_id: isPerParticipant ? null : form.service_id,
        service_resolution_mode: form.service_resolution_mode,
        shift_date: form.shift_date,
        start_time: form.start_time,
        end_time: form.end_time,
        required_staff: form.required_staff,
        support_ratio: form.support_ratio,
        notes: form.notes,
        team_id: house.team_id,
        // Day 13: Multi-participant support fields
        support_type: 'sil',
        shift_context_type: 'accommodation',
        linked_house_id: house.id,
        linked_participants: selectedResidents,
        linked_residents: linkedResidents,
        participant_id: primaryParticipantId, // For backward compatibility
      });
      toast.success('SIL shift created with linked residents');
      onSuccess();
      onClose();
    } catch (e) {
      notify.error(e, 'SIL_SHIFT_CREATE_FAILED');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-green-500" />
            Create SIL House Shift
          </DialogTitle>
          <DialogDescription>
            Create a shift for {house?.name} with multi-resident support
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Day 13: Linked Residents Section */}
          <LinkedResidentsPicker {...residents} />

          <div>
            <Label>Service Delivery Mode</Label>
            <Select value={form.service_resolution_mode} onValueChange={(v) => setForm({ ...form, service_resolution_mode: v })}>
              <SelectTrigger data-testid="house-shift-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PER_PARTICIPANT">Per-participant services — recommended for SIL</SelectItem>
                <SelectItem value="SINGLE">One service for whole shift — legacy</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              {form.service_resolution_mode === 'PER_PARTICIPANT'
                ? 'Each resident is billed from their own participant service item. A common service is not required.'
                : 'One common service is billed for the whole shift (legacy behaviour).'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Service {form.service_resolution_mode === 'PER_PARTICIPANT' ? '(not required)' : '*'}</Label>
              <LineItemSelect items={services} showRate={false} value={form.service_id} onChange={(v) => setForm({ ...form, service_id: v })}
                  placeholder={form.service_resolution_mode === 'PER_PARTICIPANT' ? 'Per-participant (not required)' : 'Select service'} disabled={form.service_resolution_mode === 'PER_PARTICIPANT'} testId="house-shift-service" />
            </div>
            <div>
              <Label>Shift Date *</Label>
              <TrinitiDatePicker
                value={form.shift_date}
                onChange={(v) => setForm({ ...form, shift_date: v })}
                testId="house-shift-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Start Time</Label>
              <TrinitiTimePicker
                value={form.start_time}
                onChange={(v) => setForm({ ...form, start_time: v })}
              />
            </div>
            <div>
              <Label>End Time</Label>
              <TrinitiTimePicker
                value={form.end_time}
                onChange={(v) => setForm({ ...form, end_time: v })}
              />
            </div>
            <div>
              <Label>Required Staff</Label>
              <Input
                type="number"
                min={1}
                value={form.required_staff}
                onChange={(e) => setForm({ ...form, required_staff: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Support Ratio</Label>
              <Select value={form.support_ratio} onValueChange={(v) => setForm({ ...form, support_ratio: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1:1">1:1 (High support)</SelectItem>
                  <SelectItem value="1:2">1:2</SelectItem>
                  <SelectItem value="1:3">1:3 (Standard)</SelectItem>
                  <SelectItem value="2:1">2:1 (Two workers)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-muted-foreground">
                Recommended: {house?.recommended_staff} staff based on {selectedResidents.length || house?.current_residents} residents
              </div>
            </div>
          </div>

          {/* Phase A — 1:2 billing-not-ready warning */}
          <SilRatioInlineWarning ratio={form.support_ratio} />

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Shift notes for staff"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading || loadingContext} data-testid="house-shift-submit">
              {loading ? 'Creating...' : `Create Shift (${selectedResidents.length} residents)`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const DEFAULT_SIL_SLOTS = [
  { label: 'Morning', start_time: '06:00', end_time: '14:00', enabled: true },
  { label: 'Afternoon', start_time: '14:00', end_time: '22:00', enabled: true },
  { label: 'Night', start_time: '22:00', end_time: '06:00', enabled: true },
];

function CreateRecurringHouseShiftsDialog({ house, open, onClose, services, onSuccess }) {
  const [form, setForm] = useState({
    service_id: '',
    service_resolution_mode: 'PER_PARTICIPANT',
    support_ratio: '1:3',
    start_date: '',
    end_date: '',
    pattern: 'WEEKLY',
    days_of_week: [0, 1, 2, 3, 4, 5, 6],
  });
  const [loading, setLoading] = useState(false);
  // client#17: residents the recurring shifts are for (previously never sent → shifts saved with no participant)
  const residents = useHouseResidents(open, house?.id);
  const { supportContext, loadingContext, selectedResidents } = residents;
  // client#16: editable shift times (defaults = the former fixed Morning / Afternoon / Night presets)
  const [slots, setSlots] = useState(DEFAULT_SIL_SLOTS);
  useEffect(() => { if (open) setSlots(DEFAULT_SIL_SLOTS); }, [open]);
  const updateSlot = (i, patch) => setSlots(prev => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addSlot = () => setSlots(prev => [...prev, { label: '', start_time: '09:00', end_time: '17:00', enabled: true }]);
  const removeSlot = (i) => setSlots(prev => prev.filter((_, j) => j !== i));

  const DAYS = [
    { value: 0, label: 'Mon' },
    { value: 1, label: 'Tue' },
    { value: 2, label: 'Wed' },
    { value: 3, label: 'Thu' },
    { value: 4, label: 'Fri' },
    { value: 5, label: 'Sat' },
    { value: 6, label: 'Sun' },
  ];

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter(d => d !== day)
        : [...f.days_of_week, day].sort()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isPerParticipant = form.service_resolution_mode === 'PER_PARTICIPANT';
    if ((!isPerParticipant && !form.service_id) || !form.start_date) {
      notify.warn('SIL_SHIFT_VALIDATION');
      return;
    }

    if (selectedResidents.length === 0) {
      toast.error('Select at least one resident');
      return;
    }
    if (form.days_of_week.length === 0) {
      toast.error('Select at least one day of the week');
      return;
    }
    const shifts_config = slots
      .filter(s => s.enabled)
      .map(s => ({ label: (s.label || '').trim() || 'Shift', start_time: s.start_time, end_time: s.end_time }));
    if (shifts_config.length === 0) {
      notify.warn('SIL_SHIFT_TIME_REQUIRED');
      return;
    }
    const badSlot = shifts_config.find(s => !s.start_time || !s.end_time || s.start_time === s.end_time);
    if (badSlot) {
      toast.error(`"${badSlot.label}" needs a start and end time that differ`);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/roster/sil/create-recurring', {
        house_id: house.id,
        service_id: isPerParticipant ? null : form.service_id,
        service_resolution_mode: form.service_resolution_mode,
        support_ratio: form.support_ratio,
        pattern: form.pattern,
        days_of_week: form.days_of_week,
        shifts_config,
        start_date: form.start_date,
        end_date: form.end_date || null,
        linked_participants: selectedResidents,
        participant_id: selectedResidents[0],
      });
      toast.success(`Created ${res.data.count || 0} recurring series`);
      onSuccess();
      onClose();
    } catch (e) {
      notify.error(e, 'SIL_RECURRING_FAILED');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-500" />
            Setup Recurring Shifts
          </DialogTitle>
          <DialogDescription>
            Create recurring shift patterns for {house?.name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <LinkedResidentsPicker {...residents} testIdPrefix="recurring-resident" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Service Delivery Mode</Label>
              <Select value={form.service_resolution_mode} onValueChange={(v) => setForm({ ...form, service_resolution_mode: v })}>
                <SelectTrigger data-testid="recurring-house-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PER_PARTICIPANT">Per-participant — recommended</SelectItem>
                  <SelectItem value="SINGLE">One service for whole shift — legacy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Support Ratio</Label>
              <Select value={form.support_ratio} onValueChange={(v) => setForm({ ...form, support_ratio: v })}>
                <SelectTrigger data-testid="recurring-house-ratio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1:1">1:1 (High support)</SelectItem>
                  <SelectItem value="1:2">1:2</SelectItem>
                  <SelectItem value="1:3">1:3 (Standard)</SelectItem>
                  <SelectItem value="2:1">2:1 (Two workers)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Service {form.service_resolution_mode === 'PER_PARTICIPANT' ? '(not required)' : '*'}</Label>
            <LineItemSelect items={services} showRate={false} value={form.service_id} onChange={(v) => setForm({ ...form, service_id: v })}
                  placeholder={form.service_resolution_mode === 'PER_PARTICIPANT' ? 'Per-participant (not required)' : 'Select service'} disabled={form.service_resolution_mode === 'PER_PARTICIPANT'} testId="recurring-house-service" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date *</Label>
              <TrinitiDatePicker
                value={form.start_date}
                onChange={(v) => setForm({ ...form, start_date: v })}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <TrinitiDatePicker
                value={form.end_date}
                onChange={(v) => setForm({ ...form, end_date: v })}
              />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Days of Week</Label>
            <div className="flex gap-2 flex-wrap">
              {DAYS.map(d => (
                <Button
                  key={d.value}
                  type="button"
                  variant={form.days_of_week.includes(d.value) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleDay(d.value)}
                >
                  {d.label}
                </Button>
              ))}
            </div>
          </div>

          <div data-testid="recurring-house-slots">
            <div className="flex items-center justify-between mb-2">
              <Label>Shift Times</Label>
              <Button type="button" variant="outline" size="sm" onClick={addSlot} data-testid="recurring-house-add-slot">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add time
              </Button>
            </div>
            <div className="space-y-2">
              {slots.map((s, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-2" data-testid={`recurring-house-slot-${i}`}>
                  <input type="checkbox" className="rounded" checked={s.enabled} onChange={(e) => updateSlot(i, { enabled: e.target.checked })} aria-label={`Include ${s.label || 'shift'}`} />
                  <Input value={s.label} onChange={(e) => updateSlot(i, { label: e.target.value })} placeholder="Label (e.g. Morning)" className="h-8 text-sm" />
                  <TrinitiTimePicker value={s.start_time} onChange={(v) => updateSlot(i, { start_time: v })} />
                  <TrinitiTimePicker value={s.end_time} onChange={(v) => updateSlot(i, { end_time: v })} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeSlot(i)} disabled={slots.length === 1} aria-label="Remove time">✕</Button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Each ticked time becomes its own recurring series. An end time earlier than the start runs overnight.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading || loadingContext || !supportContext?.residents?.length} data-testid="recurring-house-submit">
              {loading ? 'Creating...' : `Create Recurring Shifts (${selectedResidents.length} residents)`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HouseDetailDialog({ house, open, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && house) {
      setLoading(true);
      api.get(`/roster/sil/houses/${house.id}`)
        .then(res => setDetails(res.data.data))
        .catch((e) => notify.error(e, 'SIL_HOUSE_LOAD_FAILED'))
        .finally(() => setLoading(false));
    }
  }, [open, house]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-green-500" />
            {house?.name} - Roster Details
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : details ? (
          <div className="space-y-6">
            {/* Residents */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                Residents ({details.resident_count})
              </h3>
              {details.residents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active residents</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {details.residents.map(r => (
                    <div key={r.participant_id} className="text-sm p-2 bg-muted/50 rounded">
                      <p className="font-medium">{r.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.ndis_number} | {r.placement_type}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Team */}
            {details.team && (
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                  Assigned Team: {details.team.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {details.team.members.map(m => (
                    <Badge key={m.staff_id} variant="outline">{m.name}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Recommended Staffing */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">Recommended Staffing</h3>
              <div className="grid grid-cols-5 gap-2 text-sm">
                {Object.entries(details.recommended_staffing || {}).map(([time, count]) => (
                  <div key={time} className="text-center p-2 bg-muted/50 rounded">
                    <p className="capitalize font-medium">{time}</p>
                    <p className="text-lg font-bold text-blue-600">{count}</p>
                    <p className="text-xs text-muted-foreground">staff</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Shifts */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                Upcoming Shifts ({details.shifts?.length || 0})
              </h3>
              {details.shifts?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No upcoming shifts scheduled</p>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {details.shifts?.slice(0, 10).map(s => (
                    <div key={s.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-medium">{s.date} {s.start_time}-{s.end_time}</p>
                          <ShiftPeriodBadgeForShift shift={s} />
                        </div>
                        <p className="text-xs text-muted-foreground">{s.title}</p>
                      </div>
                      <Badge variant={
                        s.status === 'ASSIGNED' ? 'default' :
                        s.status === 'OPEN' ? 'destructive' :
                        s.status === 'PARTIALLY_FILLED' ? 'secondary' : 'outline'
                      }>
                        {s.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function SILRosterPage() {
  const { hasPermission } = useAuth();
  const canLegacyRecurring = hasPermission('roster.manage_templates');   // RP-3 R4 gate (legacy E5)
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [createShiftDialog, setCreateShiftDialog] = useState(null);
  const [createRecurringDialog, setCreateRecurringDialog] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [housesRes, servicesRes] = await Promise.all([
        api.get('/roster/sil/houses'),
        api.get('/services/catalog?mode=selection'),
      ]);
      setHouses(housesRes.data.data || []);
      setServices(servicesRes.data.data || []);
    } catch (e) {
      notify.error(e, 'SIL_ROSTER_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTotalUnfilled = () => houses.reduce((sum, h) => sum + (h.unfilled_today || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sil-roster-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Home className="w-6 h-6 text-green-500" />
            SIL House Rostering
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage rosters for Supported Independent Living houses
          </p>
        </div>
        {getTotalUnfilled() > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {getTotalUnfilled()} Unfilled Today
          </Badge>
        )}
      </div>

      {/* Phase A — Ratio Rules scope clarification */}
      <RatioRulesScopeNote />

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Houses</p>
                <p className="text-2xl font-bold">{houses.length}</p>
              </div>
              <Home className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Residents</p>
                <p className="text-2xl font-bold">{houses.reduce((s, h) => s + h.current_residents, 0)}</p>
              </div>
              <Users className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Shifts Today</p>
                <p className="text-2xl font-bold">{houses.reduce((s, h) => s + h.shifts_today, 0)}</p>
              </div>
              <Calendar className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card className={getTotalUnfilled() > 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unfilled Today</p>
                <p className={`text-2xl font-bold ${getTotalUnfilled() > 0 ? 'text-red-600' : ''}`}>
                  {getTotalUnfilled()}
                </p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${getTotalUnfilled() > 0 ? 'text-red-400' : 'text-muted-foreground/50'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading houses...</div>
      ) : houses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Home className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No SIL houses found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create houses in the Accommodation module first
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {houses.map(h => (
            <Card key={h.id} className="hover:shadow-md transition-shadow" data-testid={`house-card-${h.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg">{h.name}</h3>
                      {h.team_name && (
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          {h.team_name}
                        </Badge>
                      )}
                      {h.unfilled_today > 0 && (
                        <Badge variant="destructive">{h.unfilled_today} unfilled</Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {h.address}
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Occupancy</p>
                        <div className="flex items-center gap-2">
                          <Progress value={h.occupancy_rate} className="h-2 flex-1" />
                          <span className="text-sm font-medium">{h.current_residents}/{h.capacity}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Shifts Today</p>
                        <p className="text-lg font-semibold">{h.shifts_today}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Recommended Staff</p>
                        <p className="text-lg font-semibold text-blue-600">{h.recommended_staff}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        {h.unfilled_today === 0 ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="w-4 h-4" /> Covered
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            <AlertTriangle className="w-4 h-4" /> Needs Staff
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button size="sm" onClick={() => setCreateShiftDialog(h)} data-testid={`create-shift-${h.id}`}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Shift
                    </Button>
                    {canLegacyRecurring && (   // RP-3 R4: legacy series engine — Admin / Roster Manager only
                      <Button size="sm" variant="outline" onClick={() => setCreateRecurringDialog(h)} data-testid={`setup-recurring-${h.id}`}>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Setup Recurring
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setDetailsDialog(h)}>
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {createShiftDialog && (
        <CreateHouseShiftDialog
          house={createShiftDialog}
          open={!!createShiftDialog}
          onClose={() => setCreateShiftDialog(null)}
          services={services}
          onSuccess={fetchData}
        />
      )}

      {createRecurringDialog && canLegacyRecurring && (
        <CreateRecurringHouseShiftsDialog
          house={createRecurringDialog}
          open={!!createRecurringDialog}
          onClose={() => setCreateRecurringDialog(null)}
          services={services}
          onSuccess={fetchData}
        />
      )}

      {detailsDialog && (
        <HouseDetailDialog
          house={detailsDialog}
          open={!!detailsDialog}
          onClose={() => setDetailsDialog(null)}
        />
      )}
    </div>
  );
}
